/* Persona team order builder — Paper 01.G03 · Teams, 08 + States & Logic.
   Steps swap in place (200ms fade), selections survive BACK, the summary
   follows every change, only Name and Email validate (on blur and on submit).
   The mobile sticky CTA shows once the hero CTA has scrolled away and hides
   while the builder is on screen. */
(() => {
  const FADE = 200;
  const KEY_STATE = 'ps-tb-state';
  const KEY_SENT = 'ps-tb-sent';
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const store = {
    get(k) { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
    del(k) { try { sessionStorage.removeItem(k); } catch (e) { /* private mode */ } },
  };

  class Builder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      root.classList.add('is-js');
      this.unset = root.dataset.unset || 'Not set';
      this.done = root.querySelector('[data-ps-tb-done]');
      this.sticky = root.querySelector('[data-ps-tb-sticky]');
      this.bindLinks();

      if (this.done) {
        this.fillSent();
        this.hideSticky(true);
        return;
      }
      if (!this.form) return;

      this.steps = [...root.querySelectorAll('[data-ps-step]')];
      this.navs = [...root.querySelectorAll('[data-ps-stepnav]')];
      this.fill = root.querySelector('[data-ps-fill]');
      this.current = 1;

      root.querySelectorAll('[data-ps-go]').forEach((b) => b.addEventListener('click', () => this.go(+b.dataset.psGo)));
      this.form.addEventListener('change', (e) => this.onChange(e));
      this.form.addEventListener('input', (e) => {
        if (e.target.matches('[data-ps-sum="team"]')) this.summary();
        if (e.target.matches('[data-ps-req]')) {
          const f = e.target.closest('[data-ps-field]');
          if (f && f.classList.contains('is-error')) this.validate(e.target);
        }
        this.hideSticky(true);
      });
      this.form.querySelectorAll('[data-ps-req]').forEach((i) => i.addEventListener('blur', () => {
        if (i.value.trim() !== '' || i.dataset.touched) this.validate(i);
        i.dataset.touched = '1';
      }));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      if (root.querySelector('[data-ps-tb-fail]')) {
        this.restore();
        this.go(3, false);
      }
      this.summary();
      this.initSticky();
    }

    bindLinks() {
      const id = this.root.id;
      document.addEventListener('click', (e) => {
        const a = e.target.closest(`a[href="#${id}"]`);
        if (!a) return;
        e.preventDefault();
        this.hideSticky(true);
        if (this.steps && this.current !== 1) this.go(1, false);
        this.root.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        const first = this.root.querySelector('[data-ps-first]');
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 450);
        history.replaceState(history.state, '', `#${id}`);
      });
    }

    go(n, animate = true) {
      if (n === this.current && animate) return;
      const from = this.steps[this.current - 1];
      const to = this.steps[n - 1];
      const swap = () => {
        this.steps.forEach((s) => s.classList.remove('is-active', 'is-fading'));
        to.classList.add('is-active');
        this.current = n;
        this.navs.forEach((nav, i) => {
          nav.classList.toggle('is-active', i + 1 === n);
          nav.classList.toggle('is-done', i + 1 < n);
          if (i + 1 === n) nav.setAttribute('aria-current', 'step'); else nav.removeAttribute('aria-current');
        });
        if (this.fill) this.fill.style.width = `${(n / this.steps.length) * 100}%`;
        const focusable = to.querySelector('input, button, textarea');
        if (animate && focusable) focusable.focus({ preventScroll: true });
      };
      if (!animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return swap();
      from.classList.add('is-fading');
      setTimeout(swap, FADE);
    }

    onChange(e) {
      const t = e.target;
      if (t.dataset.psMulti) {
        const group = [...this.form.querySelectorAll(`[data-ps-multi="${t.dataset.psMulti}"]`)];
        if (t.checked && t.hasAttribute('data-ps-unsure')) group.forEach((i) => { if (i !== t) i.checked = false; });
        else if (t.checked) group.forEach((i) => { if (i.hasAttribute('data-ps-unsure')) i.checked = false; });
        const hidden = this.form.querySelector(`[data-ps-join="${t.dataset.psMulti}"]`);
        if (hidden) hidden.value = group.filter((i) => i.checked).map((i) => i.value).join(', ');
      }
      if (t.matches('[data-ps-date]')) {
        t.closest('label').classList.toggle('has-value', !!t.value);
        if (t.value) this.form.querySelector('[data-ps-nodeadline]').checked = false;
      }
      if (t.matches('[data-ps-nodeadline]') && t.checked) {
        const d = this.form.querySelector('[data-ps-date]');
        d.value = '';
        d.closest('label').classList.remove('has-value');
      }
      this.hideSticky(true);
      this.summary();
    }

    values() {
      const f = this.form;
      const radio = (n) => (f.querySelector(`input[name="contact[${n}]"]:checked`) || {}).value || '';
      const date = f.querySelector('[data-ps-date]').value;
      let when = '';
      if (date) {
        const d = new Date(`${date}T12:00:00`);
        when = isNaN(d) ? date : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      } else if (f.querySelector('[data-ps-nodeadline]').checked) {
        when = f.querySelector('[data-ps-nodeadline]').nextElementSibling.textContent.trim();
      }
      const qty = radio('Quantity');
      return {
        sport: radio('Sport'),
        team: f.querySelector('[data-ps-sum="team"]').value.trim(),
        qty: /\d/.test(qty) ? `${qty} pieces` : qty,
        date: when,
        pieces: f.querySelector('[data-ps-join="pieces"]').value.replace(/, /g, ' + '),
        finish: radio('Finish'),
        perso: f.querySelector('[data-ps-join="perso"]').value.replace(/, /g, ' + '),
      };
    }

    summary() {
      const v = this.values();
      Object.keys(v).forEach((k) => {
        const row = this.root.querySelector(`[data-ps-row="${k}"]`);
        if (!row) return;
        const dd = row.querySelector('dd');
        if (!row.dataset.empty) row.dataset.empty = dd.textContent;
        dd.textContent = v[k] || row.dataset.empty;
        row.classList.toggle('is-set', !!v[k]);
        if (['pieces', 'finish', 'perso'].includes(k)) row.hidden = !v[k];
      });
      const line = this.root.querySelector('[data-ps-mline]');
      if (line) {
        const parts = [v.sport, v.qty, v.date].filter(Boolean);
        line.textContent = parts.length ? parts.join(' · ') : this.unset;
      }
    }

    validate(input) {
      const field = input.closest('[data-ps-field]');
      const err = field.querySelector('.ps-tb__err');
      const val = input.value.trim();
      let msg = '';
      if (input.dataset.psReq === 'email') { if (!EMAIL.test(val)) msg = this.root.dataset.errEmail; }
      else if (!val) msg = this.root.dataset.errName;
      field.classList.toggle('is-error', !!msg);
      input.setAttribute('aria-invalid', msg ? 'true' : 'false');
      err.textContent = msg;
      err.hidden = !msg;
      return !msg;
    }

    onSubmit(e) {
      const reqs = [...this.form.querySelectorAll('[data-ps-req]')];
      const bad = reqs.filter((i) => !this.validate(i));
      if (bad.length) {
        e.preventDefault();
        if (this.current !== 3) this.go(3, false);
        bad[0].focus();
        return;
      }
      const btn = this.form.querySelector('[data-ps-submit]');
      btn.classList.add('is-sending');
      btn.setAttribute('aria-disabled', 'true');
      btn.innerHTML = btn.dataset.sending;
      // keep what was sent for the completion card, and every value in case the post fails
      const labels = {};
      this.root.querySelectorAll('[data-ps-row]').forEach((r) => { labels[r.dataset.psRow] = r.querySelector('dt').textContent; });
      const v = this.values();
      store.set(KEY_SENT, Object.keys(v).filter((k) => v[k] && k !== 'team').map((k) => [labels[k], v[k]]));
      const state = {};
      new FormData(this.form).forEach((val, k) => { if (k.startsWith('contact[')) state[k] = val; });
      state.__multi = [...this.form.querySelectorAll('[data-ps-multi]:checked')].map((i) => `${i.dataset.psMulti}|${i.value}`);
      store.set(KEY_STATE, state);
    }

    restore() {
      const state = store.get(KEY_STATE);
      if (!state) return;
      Object.entries(state).forEach(([k, val]) => {
        if (k === '__multi') return;
        this.form.querySelectorAll(`[name="${CSS.escape(k)}"]`).forEach((el) => {
          if (el.type === 'radio' || el.type === 'checkbox') el.checked = el.value === val;
          else if (el.type !== 'hidden' || k.includes('Pieces') || k.includes('Personalization')) el.value = val;
        });
      });
      (state.__multi || []).forEach((m) => {
        const [g, val] = m.split('|');
        this.form.querySelectorAll(`[data-ps-multi="${g}"]`).forEach((i) => { if (i.value === val) i.checked = true; });
      });
      const d = this.form.querySelector('[data-ps-date]');
      if (d && d.value) d.closest('label').classList.add('has-value');
    }

    fillSent() {
      const rows = store.get(KEY_SENT);
      store.del(KEY_SENT);
      store.del(KEY_STATE);
      const box = this.root.querySelector('[data-ps-tb-sent]');
      if (!rows || !rows.length || !box) return;
      const dl = box.querySelector('[data-ps-tb-sent-rows]');
      rows.forEach(([k, v]) => {
        const div = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = k;
        dd.textContent = v;
        div.append(dt, dd);
        dl.append(div);
      });
      box.hidden = false;
    }

    initSticky() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;
      const heroCta = document.querySelector('[data-ps-hero-cta]');
      let heroOut = !heroCta;
      let builderIn = false;
      const update = () => {
        const on = heroOut && !builderIn && !this.stickyGone;
        this.sticky.classList.toggle('is-on', on);
        this.sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
        this.sticky.querySelector('a').tabIndex = on ? 0 : -1;
      };
      this.updateSticky = update;
      if (heroCta) new IntersectionObserver(([en]) => { heroOut = !en.isIntersecting && en.boundingClientRect.top < 0; update(); }).observe(heroCta);
      new IntersectionObserver(([en]) => { builderIn = en.isIntersecting; update(); }, { rootMargin: '0px 0px -30% 0px' })
        .observe(this.root.querySelector('.ps-tb__form') || this.root);
    }

    hideSticky(forGood) {
      // the sticky bar only hides for good on completion; typing just hides it while the builder is in view
      if (forGood && this.done) this.stickyGone = true;
      if (this.updateSticky) this.updateSticky();
      else if (this.sticky && this.done) this.sticky.remove();
    }
  }

  const boot = () => document.querySelectorAll('[data-ps-tb]').forEach((el) => {
    if (!el.psTb) el.psTb = new Builder(el);
  });
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
